import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Crown, X, Clock } from 'lucide-react';

/**
 * TrialExpiryBanner — Shown to users on day 2 and 3 of the 3-day trial.
 *
 *   - Day 2 (2 days left): soft amber tone, dismissible per session.
 *   - Day 1 (1 day left or last day): stronger rose tone, persistent (cannot be permanently dismissed).
 *   - Hidden if user is premium or trial has ended.
 */
const DISMISS_KEY_PREFIX = 'livon_trial_banner_dismissed_';

export default function TrialExpiryBanner() {
  const { isPremium, isTrial, trialDaysLeft } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  // The banner is keyed by the day count so dismissing on day 2 still shows on day 1.
  const dayKey = typeof trialDaysLeft === 'number' ? trialDaysLeft : null;
  const storageKey = dayKey != null ? `${DISMISS_KEY_PREFIX}${dayKey}` : null;

  useEffect(() => {
    if (!storageKey) return;
    try {
      setDismissed(sessionStorage.getItem(storageKey) === '1');
    } catch (e) {
      setDismissed(false);
    }
  }, [storageKey]);

  // Only show during trial, when within last 2 days, and not premium
  if (isPremium || !isTrial) return null;
  if (typeof trialDaysLeft !== 'number') return null;
  if (trialDaysLeft > 2 || trialDaysLeft <= 0) return null;

  const isLastDay = trialDaysLeft <= 1;

  // Last day cannot be dismissed (we want maximum visibility), other days can
  const canDismiss = !isLastDay;
  if (canDismiss && dismissed) return null;

  const handleDismiss = () => {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch (e) {
      // ignore
    }
    setDismissed(true);
  };

  const palette = isLastDay
    ? {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        text: 'text-rose-900',
        sub: 'text-rose-700',
        ring: 'bg-rose-100',
        icon: 'text-rose-600',
        cta: 'bg-rose-600 hover:bg-rose-700 text-white',
      }
    : {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-900',
        sub: 'text-amber-700',
        ring: 'bg-amber-100',
        icon: 'text-amber-600',
        cta: 'bg-amber-600 hover:bg-amber-700 text-white',
      };

  const headline = isLastDay
    ? 'Sista dagen av din provperiod'
    : `${trialDaysLeft} dagar kvar av din provperiod`;

  const sub = isLastDay
    ? 'Köp Livon för 299 kr så behåller du allt utan avbrott.'
    : 'Lås upp Livon med en engångsbetalning på 299 kr.';

  return (
    <div
      className={`${palette.bg} ${palette.border} border-b px-4 py-3 flex items-center gap-3`}
      role="region"
      aria-label="Provperiod tar snart slut"
      data-testid="trial-expiry-banner"
    >
      <div className={`w-9 h-9 rounded-full ${palette.ring} flex items-center justify-center shrink-0`}>
        <Clock className={`w-4 h-4 ${palette.icon}`} />
      </div>

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${palette.text}`} data-testid="trial-banner-headline">
          {headline}
        </p>
        <p className={`text-xs ${palette.sub} hidden sm:block`}>{sub}</p>
      </div>

      <Link
        to="/subscription"
        className={`inline-flex items-center gap-1.5 ${palette.cta} text-sm font-medium px-4 py-2 rounded-full transition-colors shrink-0`}
        data-testid="trial-banner-cta"
      >
        <Crown className="w-4 h-4" />
        <span>Köp 299 kr</span>
      </Link>

      {canDismiss && (
        <button
          onClick={handleDismiss}
          className={`p-1.5 rounded-full hover:bg-black/5 ${palette.sub} shrink-0`}
          aria-label="Stäng"
          data-testid="trial-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
